import { escapeHtml, printHtmlDocument } from './printHtmlDocument'
import { resolvePatronIdentity, clubOrVisitorIdLine } from './patronIdentity'

function parseOcrData(raw) {
  if (!raw) return null
  if (typeof raw === 'object') return raw
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

function fmtDob(val) {
  if (!val) return '—'
  const s = String(val).trim()
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`
  const dmy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/)
  if (dmy) return `${dmy[1].padStart(2, '0')}/${dmy[2].padStart(2, '0')}/${dmy[3]}`
  const parsed = new Date(s)
  if (!Number.isNaN(parsed.getTime())) {
    const dd = String(parsed.getUTCDate()).padStart(2, '0')
    const mm = String(parsed.getUTCMonth() + 1).padStart(2, '0')
    const yyyy = parsed.getUTCFullYear()
    return `${dd}/${mm}/${yyyy}`
  }
  return s.slice(0, 32)
}

function amlStatusHtml(item) {
  if (item?.aml_check_id == null) return '<span class="muted">—</span>'
  const pep = item.pep_match === true || item.pep_match === 1
  const sanctions = item.sanctions_match === true || item.sanctions_match === 1
  if (!pep && !sanctions) return '<span class="badge ok">Clear</span>'
  const parts = []
  if (pep) parts.push('<span class="badge warn">PEP</span>')
  if (sanctions) parts.push('<span class="badge bad">Sanctions</span>')
  return parts.join(' ')
}

function ocrSectionHtml(ocrData) {
  const ocr = parseOcrData(ocrData)
  if (!ocr) return ''
  const rows = [
    ['Document type', ocr.document_type || '—'],
    ['Licence / document no.', ocr.document_number || '—'],
    ['Card ID (RTA)', ocr.document_card_number || ocr.licence_card_number || ocr.rta_number || '—'],
    ['Given name', ocr.given_name || '—'],
    ['Family name', ocr.family_name || '—'],
    ['Date of birth', fmtDob(ocr.date_of_birth)],
    ['Expiry', fmtDob(ocr.expiry_date)],
    ['State of issue', ocr.state_of_issue || '—'],
    ['Address', ocr.address_line || '—'],
    ['Suburb', ocr.address_suburb || '—'],
    ['Postcode', ocr.address_postcode || '—'],
    ['Country', ocr.country_code || '—'],
    ['OCR source', ocr.ocr_source || '—'],
    ['Captured', ocr.captured_at ? new Date(ocr.captured_at).toLocaleString() : '—'],
  ]
  const hasContent = rows.some(([, v]) => v && v !== '—')
  if (!hasContent) return ''
  const rowsHtml = rows
    .map(
      ([label, value]) =>
        `<div><dt>${escapeHtml(label)}</dt><dd>${escapeHtml(value)}</dd></div>`
    )
    .join('')
  return `<section><h2>ID extract (retained after image purge)</h2><dl class="meta">${rowsHtml}</dl></section>`
}

function attachmentsSectionHtml(item, imagePreviews = {}) {
  const files = Array.isArray(item?.attachments) ? item.attachments : []
  if (files.length === 0) return '<section><h2>Attachments</h2><p class="muted">No attachments on file.</p></section>'

  const listHtml = files
    .map((filename) => `<li>${escapeHtml(filename)}</li>`)
    .join('')

  const imageHtml = files
    .filter((filename) => /\.(jpg|jpeg|png|gif|webp)$/i.test(filename))
    .map((filename) => {
      const src = imagePreviews[`${item.id}_${filename}`]
      if (!src) return ''
      return `<figure class="photo"><figcaption>${escapeHtml(filename)}</figcaption><img src="${escapeHtml(src)}" alt="${escapeHtml(filename)}" /></figure>`
    })
    .filter(Boolean)
    .join('')

  return `<section><h2>Attachments</h2><ul class="files">${listHtml}</ul>${imageHtml || ''}</section>`
}

/**
 * Print a KYC registry record (same pattern as UAR print in Incident Monitoring).
 * @param {object} item KYC row
 * @param {{ venueLabel?: string, imagePreviews?: Record<string,string>, isClub?: boolean, retentionMessage?: string|null, onError?: (msg:string)=>void }} [options]
 */
export function printKycRecord(item, options = {}) {
  if (!item) return
  const {
    venueLabel = item.venue_id || '—',
    imagePreviews = {},
    isClub = true,
    retentionMessage = null,
    onError = () => {},
  } = options

  const identity = resolvePatronIdentity(item)
  const idLine = clubOrVisitorIdLine(identity, isClub)
  const patronParts = [
    item.member_name ? escapeHtml(item.member_name) : '',
    identity.patron_id ? escapeHtml(identity.patron_id) : '',
    idLine ? `${escapeHtml(idLine.label)} ${escapeHtml(idLine.value)}` : '',
  ].filter(Boolean)

  const payout =
    item.payout_amount != null && item.payout_amount !== ''
      ? `$${Number(item.payout_amount).toLocaleString()}`
      : '—'

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>KYC ${escapeHtml(item.id || '')}</title>
  <style>
    @page { margin: 16mm; }
    * { box-sizing: border-box; }
    body { font-family: Georgia, "Times New Roman", serif; color: #1a1a1a; margin: 0; padding: 24px; line-height: 1.45; }
    .brand { font-size: 11px; letter-spacing: 0.14em; text-transform: uppercase; color: #0d7377; font-family: system-ui, sans-serif; font-weight: 700; }
    h1 { font-size: 22px; margin: 6px 0 4px; font-weight: 700; }
    .sub { color: #555; font-size: 13px; margin-bottom: 18px; font-family: system-ui, sans-serif; }
    .meta { display: grid; grid-template-columns: 1fr 1fr; gap: 10px 24px; border: 1px solid #d8d8d8; border-radius: 8px; padding: 14px 16px; margin-bottom: 18px; font-family: system-ui, sans-serif; font-size: 13px; }
    .meta dt { color: #666; font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; margin: 0; }
    .meta dd { margin: 2px 0 0; font-weight: 600; }
    section { margin-bottom: 16px; page-break-inside: avoid; }
    section h2 { font-size: 12px; text-transform: uppercase; letter-spacing: 0.06em; color: #0d7377; font-family: system-ui, sans-serif; margin: 0 0 6px; }
    section p { margin: 0; white-space: pre-wrap; font-size: 14px; }
    .badge { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 11px; font-weight: 600; font-family: system-ui, sans-serif; }
    .badge.ok { background: #ecfdf5; color: #047857; }
    .badge.warn { background: #fffbeb; color: #b45309; }
    .badge.bad { background: #fef2f2; color: #b91c1c; }
    .files { margin: 0; padding-left: 18px; font-family: system-ui, sans-serif; font-size: 13px; }
    .photo { margin: 10px 0; page-break-inside: avoid; }
    .photo img { max-width: 100%; max-height: 320px; border: 1px solid #ddd; border-radius: 6px; }
    .photo figcaption { font-size: 11px; color: #666; margin-bottom: 4px; font-family: system-ui, sans-serif; }
    .notice { font-size: 12px; color: #0f766e; background: #f0fdfa; border: 1px solid #99f6e4; border-radius: 6px; padding: 8px 10px; margin-bottom: 14px; font-family: system-ui, sans-serif; }
    .muted { color: #777; font-size: 13px; font-family: system-ui, sans-serif; }
    .footer { margin-top: 28px; padding-top: 10px; border-top: 1px solid #ddd; font-size: 11px; color: #777; font-family: system-ui, sans-serif; }
    @media print { body { padding: 0; } .no-print { display: none !important; } }
  </style>
</head>
<body>
  <div class="brand">PaySafeGo · KYC record</div>
  <h1>Know Your Customer verification</h1>
  <p class="sub">Record <strong>${escapeHtml(item.id || '—')}</strong></p>
  ${retentionMessage ? `<p class="notice">${escapeHtml(retentionMessage)}</p>` : ''}
  <dl class="meta">
    <div><dt>Performed</dt><dd>${escapeHtml(item.performed_at ? new Date(item.performed_at).toLocaleString() : '—')}</dd></div>
    <div><dt>Performed by</dt><dd>${escapeHtml(item.performed_by || '—')}</dd></div>
    <div><dt>Patron</dt><dd>${patronParts.join('<br/>') || '—'}</dd></div>
    <div><dt>Reason</dt><dd>${escapeHtml(item.reason || '—')}</dd></div>
    <div><dt>Venue</dt><dd>${escapeHtml(venueLabel)}</dd></div>
    <div><dt>Payout amount</dt><dd>${escapeHtml(payout)}</dd></div>
    <div><dt>Related case</dt><dd>${escapeHtml(item.related_case_id || '—')}</dd></div>
    <div><dt>AML / PEP</dt><dd>${amlStatusHtml(item)}</dd></div>
  </dl>
  <section><h2>Notes</h2><p>${escapeHtml(item.notes?.trim() ? item.notes : '—')}</p></section>
  ${ocrSectionHtml(item.ocr_data)}
  ${attachmentsSectionHtml(item, imagePreviews)}
  <p class="footer">Printed ${escapeHtml(new Date().toLocaleString())} from PaySafeGo KYC Registry.</p>
</body>
</html>`

  printHtmlDocument(html, { onError })
}
